import { useEffect, useState } from 'react'
import { ErroreApi } from '../servizi/api.ts'
import * as posta from '../servizi/posta.ts'
import { Attesa, Avviso, Intestazione } from '../componenti/Pezzi.tsx'
import { Bottone, Campo, Riquadro } from '../componenti/base.tsx'
import { EmailRecupero } from '../componenti/EmailRecupero.tsx'
import { AppTelefono } from '../componenti/AppTelefono.tsx'
import { TelefoniConnessi } from '../componenti/TelefoniConnessi.tsx'
import { ComputerRicordati } from '../componenti/ComputerRicordati.tsx'
import { CalendarioAbbonamento } from '../componenti/CalendarioAbbonamento.tsx'

/**
 * «Il mio profilo» (508 e 520, decisioni di Davide dell'1/11/2026).
 *
 * Nome, email e sede principale li scrive l'amministrazione e qui si leggono. Cellulare e telefono
 * d'ufficio li scrive la persona, e solo lei: entrano nella firma delle email, di cui la pagina mostra
 * l'anteprima a chi usa la posta. La validazione e' leggera, come nell'API: cifre, spazi, + / e -.
 * L'email personale di recupero (520) la imposta e la cambia la persona, con il codice mandato
 * all'indirizzo nuovo (componenti/EmailRecupero.tsx). «Il calendario in Outlook e sul telefono» (561) porta
 * la propria agenda o le proprie mediazioni in un calendario esterno (componenti/CalendarioAbbonamento.tsx).
 */

const messaggio = (e: unknown) =>
  e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.'

const TELEFONO = /^[0-9 +/-]*$/

export function Profilo() {
  const [profilo, setProfilo] = useState<posta.Profilo | null>(null)
  const [cellulare, setCellulare] = useState('')
  const [ufficio, setUfficio] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  function carica() {
    return posta.leggiProfilo().then((p) => {
      setProfilo(p)
      setCellulare(p.cellulare ?? '')
      setUfficio(p.telefono_ufficio ?? '')
    })
  }

  useEffect(() => { carica().catch((e) => setErrore(messaggio(e))) }, [])

  const erroreCellulare = TELEFONO.test(cellulare) ? undefined : 'Solo cifre, spazi e i segni + / -.'
  const erroreUfficio = TELEFONO.test(ufficio) ? undefined : 'Solo cifre, spazi e i segni + / -.'
  const cambiato = !!profilo && (cellulare.trim() !== (profilo.cellulare ?? '') || ufficio.trim() !== (profilo.telefono_ufficio ?? ''))

  async function salva() {
    setErrore(null); setEsito(null)
    setInCorso(true)
    try {
      await posta.salvaTelefoni(cellulare.trim() || null, ufficio.trim() || null)
      await carica()
      setEsito('Fatto: i numeri sono salvati e la firma è aggiornata.')
    } catch (e) { setErrore(messaggio(e)) } finally { setInCorso(false) }
  }

  if (!profilo) return errore ? <Avviso tipo="errore">{errore}</Avviso> : <Attesa testo="Caricamento…" />

  return (
    <>
      <Intestazione titolo="Il mio profilo" />

      <Riquadro titolo="I tuoi dati">
        <dl>
          <div className="dato"><dt>Nome</dt><dd>{profilo.nome} {profilo.cognome}</dd></div>
          <div className="dato"><dt>Email</dt><dd>{profilo.email}</dd></div>
          <div className="dato">
            <dt>Sede principale</dt>
            <dd>
              {profilo.sede
                ? <>{profilo.sede.nome}{profilo.sede.indirizzo ? ` · ${profilo.sede.indirizzo}` : ''}</>
                : <span className="tenue">nessuna</span>}
            </dd>
          </div>
        </dl>
        <p className="piccolo tenue">
          Nome, email e sede li cambia l’amministrazione: se non sono giusti, scrivile.
        </p>
      </Riquadro>

      <Riquadro titolo="Telefoni">
        <p className="piccolo tenue">
          Compaiono nella firma delle tue email. Li vedi e li cambi solo tu; lascia vuoto il numero che
          non vuoi far vedere.
        </p>
        {errore && <Avviso tipo="errore">{errore}</Avviso>}
        {esito && <Avviso tipo="successo">{esito}</Avviso>}
        <form onSubmit={(e) => { e.preventDefault(); void salva() }} noValidate>
          <div className="griglia-modulo">
            <Campo etichetta="Cellulare" tipo="tel" modalitaTastiera="tel" maxLength={40} autoComplete="tel"
                   valore={cellulare} cambia={setCellulare} errore={erroreCellulare}
                   segnaposto="+39 333 1234567" />
            <Campo etichetta="Telefono d’ufficio" tipo="tel" modalitaTastiera="tel" maxLength={40}
                   valore={ufficio} cambia={setUfficio} errore={erroreUfficio}
                   segnaposto="075 1234567" />
          </div>
          <div className="azioni-modulo">
            <Bottone variante="primario" tipo="submit" inCorso={inCorso}
                     disabled={!cambiato || !!erroreCellulare || !!erroreUfficio}>
              Salva i numeri
            </Bottone>
          </div>
        </form>
      </Riquadro>

      {profilo.usa_posta && (
        <Riquadro titolo="La tua firma">
          <p className="piccolo tenue">
            Così si apre ogni email che scrivi dal gestionale, dalla tua casella e da quelle condivise che
            non hanno una firma propria. Nel messaggio la puoi modificare; i testi comuni (denominazione,
            sede legale, avvisi di riservatezza) li decide l’amministrazione.
          </p>
          <pre className="posta-testo testo-preformattato profilo-firma">{profilo.firma}</pre>
        </Riquadro>
      )}

      <EmailRecupero />
      <AppTelefono />
      <TelefoniConnessi />
      <ComputerRicordati />
      <CalendarioAbbonamento />
    </>
  )
}
